'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { BlogPostMetadata } from '@/lib/mdx';

interface Category {
  id: string;
  label: string;
}

interface BlogPageClientProps {
  posts: BlogPostMetadata[];
  categories: Category[];
  postCategories: Record<string, string>;
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`flex-none rounded-full border px-4 h-10 text-sm font-medium transition-colors whitespace-nowrap ${
        active
          ? 'border-foreground bg-foreground text-background'
          : 'border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

export default function BlogPageClient({ posts, categories, postCategories }: BlogPageClientProps) {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const filtered = useMemo(() => {
    if (activeCategory === 'all') return posts;
    return posts.filter((p) => postCategories[p.slug] === activeCategory);
  }, [activeCategory, posts, postCategories]);

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: posts.length };
    for (const cat of categories) {
      map[cat.id] = posts.filter((p) => postCategories[p.slug] === cat.id).length;
    }
    return map;
  }, [posts, categories, postCategories]);

  const activeLabel = activeCategory === 'all'
    ? null
    : categories.find((c) => c.id === activeCategory)?.label;

  const visibleCategories = categories.filter((c) => (counts[c.id] ?? 0) > 0);

  return (
    <div className="flex flex-col gap-8 lg:flex-row lg:gap-16 items-start w-full max-w-full">
      {/* Post list */}
      <div className="flex-1 min-w-0 w-full">
        {/* Mobile topic filter */}
        <div className="lg:hidden mb-6">
          <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-3">
            Topics
          </p>
          <div className="relative -mx-4 sm:-mx-6">
            <div className="flex gap-2 overflow-x-auto px-4 sm:px-6 pb-3 scrollbar-hide">
              <Pill active={activeCategory === 'all'} onClick={() => setActiveCategory('all')}>
                All posts ({counts.all})
              </Pill>
              {visibleCategories.map((cat) => (
                <Pill key={cat.id} active={activeCategory === cat.id} onClick={() => setActiveCategory(cat.id)}>
                  {cat.label} ({counts[cat.id]})
                </Pill>
              ))}
            </div>
            <div className="absolute right-0 top-0 bottom-3 w-10 bg-gradient-to-l from-background to-transparent pointer-events-none" />
          </div>
        </div>

        {activeLabel && (
          <div className="mb-6 sm:mb-8 flex items-center gap-3">
            <h2 className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
              {activeLabel}
            </h2>
            <button
              onClick={() => setActiveCategory('all')}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
            >
              Clear
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="text-muted-foreground py-12">No posts in this category yet.</p>
        ) : (
          <div className="grid gap-4 sm:gap-6 lg:gap-8">
            {filtered.map((post) => (
              <article
                key={post.slug}
                className="group bg-card border border-border rounded-2xl p-4 sm:p-5 lg:p-0 lg:bg-transparent lg:border-0 lg:border-b lg:border-border lg:rounded-none lg:last:border-b-0 transition-all hover:shadow-sm lg:hover:shadow-none"
              >
                <Link href={`/blog/${post.slug}`} className="block">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold tracking-[-0.01em] leading-snug text-foreground mb-2 sm:mb-3 group-hover:text-foreground/80 transition-colors break-words">
                    {post.title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
                    <time dateTime={post.date}>
                      {new Date(post.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </time>
                    <span className="hidden sm:inline">•</span>
                    <span>{post.readingTime}</span>
                  </div>
                  <p className="text-sm sm:text-base leading-relaxed text-muted-foreground mb-3 sm:mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <span className="inline-flex items-center text-sm sm:text-base text-foreground font-medium group-hover:gap-2 transition-all">
                    Read more
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 ml-1 group-hover:ml-2 transition-all"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </span>
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <aside className="hidden lg:block w-56 shrink-0 sticky top-28">
        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-4">
          Topics
        </p>
        <ul className="space-y-1">
          <li>
            <button
              onClick={() => setActiveCategory('all')}
              className={`w-full text-left text-sm py-1.5 transition-colors flex justify-between items-center gap-2 ${
                activeCategory === 'all'
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>All posts</span>
              <span className="text-xs opacity-50">{counts.all}</span>
            </button>
          </li>
          {visibleCategories.map((cat) => (
            <li key={cat.id}>
              <button
                onClick={() => setActiveCategory(cat.id)}
                className={`w-full text-left text-sm py-1.5 transition-colors flex justify-between items-center gap-2 ${
                  activeCategory === cat.id
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span>{cat.label}</span>
                <span className="text-xs opacity-50">{counts[cat.id]}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
